import { captureSupportScope, internalApiHeaders } from '../admin/supportTransport';
import { useState, useEffect, useCallback } from 'react';
import { currentAssessmentQuestion } from '../utils/currentAssessment';

export interface AIQuestionAssessment {
  origin: 'ai';
  runId: string;
  rationale: string;
  confidence: 'low' | 'medium' | 'high' | null;
  assessmentReason?: 'provider_output_limit';
  sourceRefs: string[];
  contextRevision: number;
  assessedAt: string;
  contentHash: string;
  basis?: {
    questionHash: string;
    foundation?: {
      type: 'vision' | 'mission' | 'company_values' | 'company_objectives';
      entityKey: string | null;
      contentHash: string;
    };
  };
  stale?: boolean;
}

export interface Question {
  id: string;
  questionTypeId: string;
  content: string;
  description?: string;
  inputType: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'boolean';
  options?: Array<{ label: string; value: string; score?: number }>;
  validationRules?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
  point: number;
  isPreset: boolean;
  displayOrder: number;
  isActive: boolean;
  responses?: Array<{
    id: string;
    response: {
      textValue?: string;
      selectedOptions?: string[];
      numericValue?: number;
      booleanValue?: boolean;
      impactLevel?: 'low' | 'medium' | 'high';
      assessment?: AIQuestionAssessment;
    };
    score: number | null;
    answeredAt: string;
  }>;
}

export interface QuestionResponse {
  questionId: string;
  entityType: string;
  entityKey?: string;
  response: {
    textValue?: string;
    selectedOptions?: string[];
    numericValue?: number;
    booleanValue?: boolean;
    impactLevel?: 'low' | 'medium' | 'high';
    assessment?: AIQuestionAssessment;
  };
}

interface UseQuestionsResult {
  questions: Question[];
  loading: boolean;
  error: string | null;
  saveResponse: (response: QuestionResponse) => Promise<boolean>;
  getQuestionResponse: (questionId: string, entityKey?: string) => any;
}

// Helper function to get auth token from Clerk
async function getAuthToken(): Promise<string | null> {
  try {
    // Access Clerk instance from window
    const clerk = (window as any).Clerk;
    if (!clerk?.user) return null;

    // Get session token
    const session = clerk.session;
    if (!session) return null;

    return await session.getToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

export function useQuestions(
  entityType: string,
  companyId: string | undefined,
  entityKey?: string
): UseQuestionsResult {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuestions = async () => {
      if (!companyId) return;

      setLoading(true);
      setError(null);

      try {
        const requestScope = captureSupportScope();
        const token = await getAuthToken();
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        const url = new URL(
          `${API_BASE_URL}/questions/entity/${entityType}/${companyId}`,
          window.location.origin
        );
        if (entityKey) {
          url.searchParams.append('entityKey', entityKey);
        }

        const response = await fetch(url.toString(), {
          headers: internalApiHeaders(
            url,
            {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            false,
            requestScope
          ),
        });

        if (!response.ok) {
          throw new Error(`Failed to load questions: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success) {
          setQuestions((data.data || []).map(currentAssessmentQuestion));
        } else {
          throw new Error(data.error || 'Failed to load questions');
        }
      } catch (err) {
        console.error('Error loading questions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load questions');
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [entityType, companyId, entityKey]);

  const saveResponse = useCallback(
    async (response: QuestionResponse): Promise<boolean> => {
      if (!companyId) return false;

      try {
        const requestScope = captureSupportScope();
        const token = await getAuthToken();
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        const apiResponse = await fetch(`${API_BASE_URL}/questions/responses/${companyId}`, {
          method: 'POST',
          headers: internalApiHeaders(
            `${API_BASE_URL}/questions/responses/${companyId}`,
            {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            false,
            requestScope
          ),
          body: JSON.stringify(response),
        });

        if (!apiResponse.ok) {
          throw new Error(`Failed to save response: ${apiResponse.statusText}`);
        }

        const data = await apiResponse.json();

        if (data.success) {
          // Update the local questions state with the new response
          setQuestions((prev) =>
            prev.map((q) => {
              if (q.id === response.questionId) {
                return {
                  ...q,
                  responses: [data.data], // Replace with the latest response
                };
              }
              return q;
            })
          );
          return true;
        } else {
          throw new Error(data.error || 'Failed to save response');
        }
      } catch (err) {
        console.error('Error saving response:', err);
        return false;
      }
    },
    [companyId]
  );

  const getQuestionResponse = useCallback(
    (questionId: string, _entityKey?: string) => {
      const question = questions.find((q) => q.id === questionId);
      if (!question || !question.responses || question.responses.length === 0) {
        return null;
      }

      // Return the most recent response (responses are ordered by answeredAt desc)
      return question.responses[0];
    },
    [questions]
  );

  return {
    questions,
    loading,
    error,
    saveResponse,
    getQuestionResponse,
  };
}
